/**
 * Shared E-Mail-Template — markengerechtes, e-mail-client-optimiertes HTML.
 *
 * `renderBrandedEmail({ brand, subject, bodyHtml })` liefert ein vollständiges,
 * "bulletproof" HTML-Dokument (XHTML 1.0 Transitional für Outlook):
 *   - role="presentation"-Tabellen-Layout, KEIN flexbox/grid
 *   - NUR Inline-CSS, Font-Stack "Montserrat/Inter, Arial, Helvetica" (web-safe
 *     Fallback, Google-Fonts nur als progressive enhancement)
 *   - max-width 600px, zentriert
 *   - versteckter Preheader
 *   - Markenkopf: echtes Logo auf weißem Grund + feine 2px-Akzentlinie in
 *     Markenfarbe (KEIN dicker bunter Balken — modern, hochwertig, viel Weißraum)
 *   - Body (bodyHtml unverändert eingebettet)
 *   - optionale schlanke Foto-Signatur (rundes Portrait + Name/Rolle/
 *     Qualifikationen) — OHNE Kontakt/Adresse; der Kontakt steht ausschließlich
 *     im Footer (keine Dopplung). NUR wenn die Marke ein Foto + Signaturnamen
 *     mitbringt (Koop = Goldstandard)
 *   - dunkler Anthrazit-Footer (#0c1825) mit (weißem) Logo +
 *     Anschrift/Kontakt/Domain in Hellgrau; Markenfarbe nur als dezenter Akzent.
 *     Der Footer ist die EINZIGE Kontaktstelle der E-Mail. KEINE USP-/
 *     Qualifikationszeile im Footer — die Qualifikationen stehen in der Signatur.
 *   - MSO-Conditionals für Outlook
 *
 * Diese Datei ist absichtlich frei von Supabase-/DOM-Abhängigkeiten, damit sie
 * sowohl im Frontend (Vorschau) als auch im Sendepfad (api/, lib/) importiert
 * werden kann. So ist die Vorschau garantiert deckungsgleich mit dem Versand.
 *
 * Import aus api/-Code MUSS die .js-Endung tragen:
 *   import { renderBrandedEmail } from "../lib/email-template.js";
 */

/** Markendaten für das Template (aus `marken` / `marken_config` / betriebe.ts). */
export interface EmailBrand {
  /** Anzeigename der Marke, z. B. "MietGo". */
  name: string;
  /** Rechtsträger, z. B. "LCS GmbH" (optional; nur wenn abweichend angezeigt). */
  legalName?: string | null;
  /** Primär-/Akzentfarbe der Marke (Hex), z. B. "#163E6E". */
  primaryColor: string;
  /**
   * Dunkler Footer-Hintergrund (Hex). Default: Anthrazit #0c1825. Kann je Marke
   * überschrieben werden (z. B. dunkles Markengrau).
   */
  footerColor?: string | null;
  /** Absolute Logo-URL (https) für den hellen Kopf. Ohne Logo → Wortmarke. */
  logoUrl?: string | null;
  /**
   * Optionaler dezenter Untertitel rechts im Kopf (klein, grau), z. B.
   * "Kfz-Sachverständigenbüro". Nur rendern, wenn gesetzt — minimal/seriös.
   */
  headerSubtitle?: string | null;
  /**
   * Absolute Logo-URL (https) für den dunklen Footer (i. d. R. weiße Variante).
   * Fehlt sie, wird der Markenname als helle Wortmarke im Footer gerendert.
   */
  footerLogoUrl?: string | null;
  /** Absolute Foto-URL (https) für die Signatur (rundes Portrait). */
  photoUrl?: string | null;
  /** Name in der Signatur, z. B. "Andreas Koop". */
  signatureName?: string | null;
  /** Rolle/Untertitel in der Signatur, z. B. "Kfz-Sachverständiger für Schäden und Bewertungen". */
  signatureRole?: string | null;
  /**
   * Optionale Qualifikationszeilen unter der Rolle (jede Zeile separat, grau).
   * Z. B. ["Techniker-Meister · ehem. Rennmechaniker", "Ausgebildet an der
   * BVSK-Akademie · Mitglied im MAS"]. KEIN Kontakt — der steht nur im Footer.
   */
  signatureQualifications?: string[] | null;
  /** Optionale kleine Schlusszeile der Signatur, z. B. "seit 2014". */
  signatureSince?: string | null;
  /** Anschrift (einzeilig). */
  address?: string | null;
  /** Kontakt-E-Mail (Absender). */
  email?: string | null;
  /** Telefon (optional). */
  phone?: string | null;
  /** Website/Domain, z. B. "https://gutachter-koop.de" oder "gutachter-koop.de". */
  website?: string | null;
  /**
   * @deprecated Wird NICHT mehr im Footer gerendert. Die Qualifikationen/USPs
   * erscheinen ausschließlich in der Signatur (signatureQualifications), um eine
   * Dopplung im Footer zu vermeiden. Feld bleibt nur aus Kompatibilitätsgründen.
   */
  footerUsp?: string | null;
  /** Optionaler Rechts-Disclaimer für die Fußzeile. */
  disclaimer?: string | null;
}

export interface RenderBrandedEmailOpts {
  brand: EmailBrand;
  subject: string;
  /** Body-HTML (bereits HTML; einfacher Text bitte vorher in <p> umwandeln). */
  bodyHtml: string;
  /** Optionaler Preheader-Text (Vorschauzeile im Posteingang). */
  preheader?: string;
}

export interface RenderBrandedEmailResult {
  html: string;
}

/** Anthrazit-Default für den dunklen Footer (CI Koop "Dark Background"). */
export const FOOTER_DARK = '#0c1825';

/** HTML-Attribut/-Text sicher escapen. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** "gutachter-koop.de" aus "https://www.gutachter-koop.de/". */
function prettyDomain(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '');
}

/** Stellt sicher, dass ein Website-Wert eine absolute URL für href ist. */
function toHref(url: string): string {
  return /^https?:\/\//.test(url) ? url : `https://${url.replace(/^\/+/, '')}`;
}

/**
 * Erkennt, ob ein Body bereits ein vollständiges, gebrandetes Dokument ist
 * (z. B. weil er schon einmal durch dieses Template gelaufen ist). Verhindert
 * doppeltes Wrappen / doppelte Fußzeilen.
 */
export function isAlreadyBranded(bodyHtml: string): boolean {
  const head = bodyHtml.slice(0, 400).toLowerCase();
  return head.includes('<!doctype') || head.includes('data-branded-email');
}

/**
 * Rendert markengerechtes, bulletproof E-Mail-HTML. Idempotent: wenn `bodyHtml`
 * bereits ein gebrandetes Dokument ist, wird es unverändert zurückgegeben.
 */
export function renderBrandedEmail(opts: RenderBrandedEmailOpts): RenderBrandedEmailResult {
  const { brand, subject } = opts;
  const bodyHtml = opts.bodyHtml ?? '';

  if (isAlreadyBranded(bodyHtml)) {
    return { html: bodyHtml };
  }

  const color = brand.primaryColor || '#163E6E';
  const footerBg = brand.footerColor || FOOTER_DARK;
  const name = escapeHtml(brand.name || 'Koop');
  const preheader = escapeHtml(opts.preheader || subject || '');

  // Font-Stack: Google-Fonts (Montserrat/Inter) nur als progressive enhancement,
  // web-safe Fallback Arial/Helvetica. Outlook nutzt via MSO-Block ohnehin Arial.
  const fontStack = "'Montserrat','Inter',Arial,'Helvetica Neue',Helvetica,sans-serif";
  const textColor = '#1a1a2e';
  const textMuted = '#64748b';

  // ─── Kopf: echtes Logo (display:block, dezent) ODER Wortmarke ──────────────
  // Modern/seriös: weißer Kopf mit viel Weißraum, Logo links, max-height 40px.
  // Rechts optional ein dezenter grauer Subtitle (z. B. "Kfz-Sachverständigenbüro").
  // Kein bunter Balken — die Markenfarbe erscheint nur als feine 2px-Linie unten.
  const headerLogo = brand.logoUrl
    ? `<img src="${escapeHtml(brand.logoUrl)}" alt="${name}" width="180" height="40" style="display:block;border:0;outline:none;text-decoration:none;height:auto;max-height:40px;width:auto;" />`
    : `<span style="font-family:${fontStack};font-size:22px;font-weight:bold;color:${escapeHtml(color)};letter-spacing:0.3px;">${name}</span>`;
  const headerSubtitle = brand.headerSubtitle
    ? `<span style="font-family:${fontStack};font-size:12px;color:#94a3b8;letter-spacing:0.3px;white-space:nowrap;">${escapeHtml(brand.headerSubtitle)}</span>`
    : '';
  const headerInner = headerSubtitle
    ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
              <tr>
                <td align="left" valign="middle">${headerLogo}</td>
                <td align="right" valign="middle" style="text-align:right;">${headerSubtitle}</td>
              </tr>
            </table>`
    : headerLogo;

  // ─── Foto-Signatur (NUR bei vorhandenem Foto + Name) ───────────────────────
  // Links rundes Portrait (~64px), rechts Name (bold, Markenblau), Rolle
  // (Textfarbe), Qualifikationen (grau) und optional eine kleine Schlusszeile.
  // BEWUSST OHNE Kontakt/Adresse — der Kontakt steht ausschließlich im Footer.
  let signatureRow = '';
  if (brand.photoUrl && brand.signatureName) {
    const sigName = escapeHtml(brand.signatureName);
    const sigRole = brand.signatureRole ? escapeHtml(brand.signatureRole) : '';
    const sigQuals = (brand.signatureQualifications ?? [])
      .filter((q) => !!q)
      .map(
        (q) =>
          `<div style="font-family:${fontStack};font-size:12px;color:${textMuted};line-height:1.5;padding-top:2px;">${escapeHtml(q)}</div>`,
      )
      .join('');
    const sigSince = brand.signatureSince
      ? `<div style="font-family:${fontStack};font-size:11px;color:${textMuted};line-height:1.4;padding-top:6px;">${escapeHtml(brand.signatureSince)}</div>`
      : '';
    signatureRow = `
        <!-- Foto-Signatur (rundes Portrait + Name/Rolle/Qualifikationen, KEIN Kontakt) -->
        <tr>
          <td style="padding:8px 40px 28px 40px;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
              <tr><td style="border-top:1px solid #eceef1;font-size:0;line-height:0;height:1px;">&nbsp;</td></tr>
              <tr>
                <td style="padding:20px 0 0 0;">
                  <table role="presentation" cellpadding="0" cellspacing="0" border="0">
                    <tr>
                      <td valign="top" style="width:64px;padding-right:16px;">
                        <img src="${escapeHtml(brand.photoUrl)}" alt="${sigName}" width="64" height="64" style="display:block;border:0;outline:none;text-decoration:none;width:64px;height:64px;border-radius:32px;-ms-interpolation-mode:bicubic;" />
                      </td>
                      <td valign="middle" style="font-family:${fontStack};">
                        <div style="font-family:${fontStack};font-size:15px;font-weight:bold;color:${escapeHtml(color)};line-height:1.3;">${sigName}</div>
                        ${sigRole ? `<div style="font-family:${fontStack};font-size:13px;color:${textColor};line-height:1.4;padding-top:3px;">${sigRole}</div>` : ''}
                        ${sigQuals}
                        ${sigSince}
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </td>
        </tr>`;
  }

  // ─── Footer (dunkler Anthrazit-Balken) — EINZIGE Kontaktstelle ─────────────
  const legalSuffix =
    brand.legalName && brand.legalName !== brand.name
      ? ` &middot; eine Marke der ${escapeHtml(brand.legalName)}`
      : '';

  const footerLink = '#cbd5e1';
  const footerContact: string[] = [];
  if (brand.address) footerContact.push(escapeHtml(brand.address));
  if (brand.phone) footerContact.push(escapeHtml(brand.phone));
  if (brand.email) {
    footerContact.push(
      `<a href="mailto:${escapeHtml(brand.email)}" style="color:${footerLink};text-decoration:none;">${escapeHtml(brand.email)}</a>`,
    );
  }
  if (brand.website) {
    footerContact.push(
      `<a href="${escapeHtml(toHref(brand.website))}" style="color:${footerLink};text-decoration:none;">${escapeHtml(prettyDomain(brand.website))}</a>`,
    );
  }

  const footerLogo = brand.footerLogoUrl
    ? `<img src="${escapeHtml(brand.footerLogoUrl)}" alt="${name}" width="150" height="36" style="display:block;border:0;outline:none;text-decoration:none;height:auto;max-height:36px;width:auto;" />`
    : `<span style="font-family:${fontStack};font-size:16px;font-weight:bold;color:#ffffff;letter-spacing:0.3px;">${name}${legalSuffix ? `<span style="font-weight:normal;color:#94a3b8;">${legalSuffix}</span>` : ''}</span>`;

  const disclaimerRow = brand.disclaimer
    ? `<tr><td style="padding:12px 0 0 0;font-family:${fontStack};font-size:11px;line-height:1.5;color:#7c8aa0;">${escapeHtml(brand.disclaimer)}</td></tr>`
    : '';

  return {
    html: `<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.0 Transitional//EN" "http://www.w3.org/TR/xhtml1/DTD/xhtml1-transitional.dtd">
<html xmlns="http://www.w3.org/1999/xhtml" lang="de" data-branded-email="1">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<meta name="x-apple-disable-message-reformatting" />
<title>${escapeHtml(subject || name)}</title>
<!--[if mso]>
<style type="text/css">
  table, td, div, p, a, span { font-family: Arial, Helvetica, sans-serif !important; }
</style>
<![endif]-->
</head>
<body style="margin:0;padding:0;background-color:#f4f5f7;-webkit-text-size-adjust:100%;-ms-text-size-adjust:100%;">
<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:#f4f5f7;">${preheader}&#8203;&#8203;&#8203;&#8203;&#8203;&#8203;&#8203;&#8203;&#8203;&#8203;&#8203;&#8203;&#8203;&#8203;&#8203;</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f4f5f7;">
  <tr>
    <td align="center" style="padding:32px 12px;">
      <!--[if mso]><table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0"><tr><td><![endif]-->
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="width:100%;max-width:600px;background-color:#ffffff;border-radius:12px;overflow:hidden;border-collapse:separate;border:1px solid #ebedf0;">
        <!-- Kopf: Logo / Wortmarke auf weißem Grund, viel Weißraum -->
        <tr>
          <td style="padding:24px 40px 24px 40px;">
            ${headerInner}
          </td>
        </tr>
        <!-- Feine 2px-Akzentlinie in Markenfarbe (statt dickem Balken) -->
        <tr><td style="height:2px;line-height:2px;font-size:2px;background-color:${escapeHtml(color)};">&nbsp;</td></tr>
        <!-- Body -->
        <tr>
          <td style="padding:32px 40px 16px 40px;font-family:${fontStack};font-size:15px;line-height:1.65;color:${textColor};">
            ${bodyHtml}
          </td>
        </tr>${signatureRow}
        <!-- Footer: dunkler Anthrazit-Balken (einzige Kontaktstelle) -->
        <tr>
          <td style="padding:0;">
            <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${escapeHtml(footerBg)};">
              <tr>
                <td style="padding:28px 40px 28px 40px;">
                  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                    <tr><td style="padding:0 0 14px 0;">${footerLogo}</td></tr>
                    <tr><td style="border-top:1px solid rgba(255,255,255,0.10);font-size:0;line-height:0;height:1px;">&nbsp;</td></tr>
                    ${
                      footerContact.length
                        ? `<tr><td style="font-family:${fontStack};font-size:12px;line-height:1.7;color:#cbd5e1;padding:14px 0 0 0;">${footerContact.join(' &middot; ')}</td></tr>`
                        : ''
                    }
                    ${disclaimerRow}
                  </table>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
      <!--[if mso]></td></tr></table><![endif]-->
    </td>
  </tr>
</table>
</body>
</html>`,
  };
}

// ─── Marken-Mapping (geteilt zwischen Vorschau und Versand) ──────────────────

/**
 * Akzent-/Primärfarbe je Marken-Name. Quelle: marken_config.farbe_primaer bzw.
 * client/src/lib/betriebe-Konvention. Koop CI = Koop-Blau #163E6E.
 * Bewusst nach marken_name (nicht business_unit) gekeyt, da der Sendepfad die
 * Marke per Name auflöst (resolveMarkenNameForBusinessUnit).
 */
export const BRAND_COLOR_BY_NAME: Record<string, string> = {
  'MietGo': '#1E6FBA',
  'Lenneglas': '#059669',
  'Kfz Gutachter Koop': '#163E6E',
  'SV Büro Koop': '#163E6E',
  'Gutachter Koop': '#163E6E',
  'LCS GmbH': '#1F2937',
};

/** Default-Markenfarbe (Koop-Blau), wenn keine spezifische Farbe bekannt ist. */
export const DEFAULT_BRAND_COLOR = '#163E6E';

/**
 * Marken-Spezialisierungen, die NICHT in `marken`/`marken_config` modelliert sind
 * (weißes Footer-Logo, fixer Anthrazit-Footer, Foto-Fallback, schlanke
 * Signatur-Texte, Kopf-Subtitle). Koop = Goldstandard.
 * Gekeyt nach marken_name. Für andere Marken greift der generische Default.
 */
export const KOOP_WHITE_LOGO_URL =
  'https://gutachter-koop.de/downloads/logos/koop-kfz-gutachten-logo-weiss-hd.png';

/** Schlanke Koop-Signatur: Rolle + Qualifikationszeilen + Schlusszeile (KEIN Kontakt). */
export const KOOP_SIGNATURE_ROLE =
  'Kfz-Sachverständiger für Schäden und Bewertungen';
export const KOOP_SIGNATURE_QUALIFICATIONS = [
  'Techniker-Meister · ehem. Rennmechaniker',
  'Ausgebildet an der BVSK-Akademie · Mitglied im MAS',
];
export const KOOP_SIGNATURE_SINCE = 'seit 2014';

/**
 * @deprecated Wird NICHT mehr im Footer gerendert (Dopplung zur Signatur).
 * Die Qualifikationen stehen ausschließlich in der Signatur
 * (KOOP_SIGNATURE_QUALIFICATIONS). Konstante bleibt nur aus
 * Kompatibilitätsgründen erhalten.
 */
export const KOOP_FOOTER_USP =
  'Kfz-Techniker-Meister · ehem. Rennmechaniker · BVSK-Akademie · Mitglied im MAS · seit 2014';

/** Dezenter Kopf-Subtitle rechts (seriös/minimal). */
export const KOOP_HEADER_SUBTITLE = 'Kfz-Sachverständigenbüro';

interface BrandExtras {
  footerLogoUrl?: string;
  footerColor?: string;
  /** Foto-Fallback, falls `marken.foto_url` leer ist. */
  photoUrl?: string;
  /** Schlanke Signatur-Defaults (Rolle/Qualifikationen/Schlusszeile). */
  signatureRole?: string;
  signatureQualifications?: string[];
  signatureSince?: string;
  /** Dezenter Kopf-Subtitle. */
  headerSubtitle?: string;
}

const KOOP_EXTRAS: BrandExtras = {
  footerLogoUrl: KOOP_WHITE_LOGO_URL,
  footerColor: FOOTER_DARK,
  photoUrl: 'https://gutachter-koop.de/images/andreas-koop-portrait.webp',
  signatureRole: KOOP_SIGNATURE_ROLE,
  signatureQualifications: KOOP_SIGNATURE_QUALIFICATIONS,
  signatureSince: KOOP_SIGNATURE_SINCE,
  headerSubtitle: KOOP_HEADER_SUBTITLE,
};

const BRAND_EXTRAS_BY_NAME: Record<string, BrandExtras> = {
  'Kfz Gutachter Koop': KOOP_EXTRAS,
  'Gutachter Koop': KOOP_EXTRAS,
  'SV Büro Koop': KOOP_EXTRAS,
};

/** Minimale Shape einer `marken`-Zeile, soweit fürs Template relevant. */
export interface MarkeRowForBrand {
  marken_name: string;
  rechtstraeger?: string | null;
  anschrift?: string | null;
  absender_email?: string | null;
  email?: string | null;
  website?: string | null;
  telefon?: string | null;
  logo_url?: string | null;
  footer_disclaimer?: string | null;
  foto_url?: string | null;
  signatur_name?: string | null;
  signatur_rolle?: string | null;
}

/**
 * Bildet eine `marken`-Zeile (+ optionale Farb-Overrides) auf `EmailBrand` ab.
 * Wird sowohl in der Freigabe-Vorschau als auch im Sendepfad genutzt, damit
 * Vorschau und Versand garantiert dieselbe Marken-Darstellung verwenden.
 * Foto/Signatur kommen aus `marken` (foto_url/signatur_name/signatur_rolle);
 * weißes Footer-Logo + Anthrazit-Footer + Foto-Fallback + schlanke Signatur-
 * Texte (Rolle/Qualifikationen/Subtitle) aus BRAND_EXTRAS_BY_NAME.
 * Kontakt erscheint ausschließlich im Footer (keine Dopplung).
 */
export function markeToEmailBrand(
  marke: MarkeRowForBrand,
  overrides?: { primaryColor?: string | null },
): EmailBrand {
  const primaryColor =
    overrides?.primaryColor ||
    BRAND_COLOR_BY_NAME[marke.marken_name] ||
    DEFAULT_BRAND_COLOR;
  const extras = BRAND_EXTRAS_BY_NAME[marke.marken_name];
  return {
    name: marke.marken_name,
    legalName: marke.rechtstraeger ?? null,
    primaryColor,
    footerColor: extras?.footerColor ?? FOOTER_DARK,
    logoUrl: marke.logo_url ?? null,
    headerSubtitle: extras?.headerSubtitle ?? null,
    footerLogoUrl: extras?.footerLogoUrl ?? null,
    photoUrl: marke.foto_url ?? extras?.photoUrl ?? null,
    signatureName: marke.signatur_name ?? null,
    signatureRole: marke.signatur_rolle ?? extras?.signatureRole ?? null,
    signatureQualifications: extras?.signatureQualifications ?? null,
    signatureSince: extras?.signatureSince ?? null,
    address: marke.anschrift ?? null,
    email: marke.absender_email ?? marke.email ?? null,
    phone: marke.telefon ?? null,
    website: marke.website ?? null,
    disclaimer: marke.footer_disclaimer ?? null,
  };
}
